import React, { useEffect, useState } from 'react';
import BrandMark from './BrandMark';
import { ArrowDown } from './Icons';
import DownloadButton from './DownloadButton';
import { PAPER } from '../content/whitepaper';

const SITE_LINKS = [
  ['What we do', '#what-we-do'],
  ['Process', '#process'],
  ['White paper', '/whitepaper/'],
  ['Research', '/research/'],
];

const PAPER_LINKS = [
  ['Contents', '#contents'],
  ['Risks', '#section-14'],
  ['References', '#section-15'],
];

const BRAND_LINKS = [
  ['Colors', '#colors'],
  ['Logos', '#logos'],
  ['Usage', '#usage'],
];

const PRODUCT_LINKS = [
  ['What it does', '#work'],
  ['Modes', '#modes'],
  ['Privacy', '#privacy'],
  ['Limits', '#limits'],
  ['Install', '#install'],
  ['FAQ', '#faq'],
];

const HEADER_PROBE = 36;

export default function Nav({ variant = 'site' }) {
  const atHome = variant === 'site';
  const onPaper = variant === 'paper';
  const onBrand = variant === 'brand';
  const links = atHome ? SITE_LINKS : onPaper ? PAPER_LINKS : onBrand ? BRAND_LINKS : PRODUCT_LINKS;
  const product = onPaper ? 'White paper' : onBrand ? 'Brand' : 'Prometheus';
  const menuLabel = atHome ? 'Primary' : product;
  const [scrolled, setScrolled] = useState(false);
  const [tone, setTone] = useState('dark');
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(window.scrollY > 8);

      let nextTone = 'dark';
      const surfaces = document.querySelectorAll('[data-nav-tone]');
      for (let i = 0; i < surfaces.length; i += 1) {
        const rect = surfaces[i].getBoundingClientRect();
        if (rect.top <= HEADER_PROBE && rect.bottom > HEADER_PROBE) {
          nextTone = surfaces[i].getAttribute('data-nav-tone');
          break;
        }
      }
      setTone(nextTone);

      // The innermost target spanning the probe line wins, so a row inside a section beats the section.
      const probe = window.innerHeight * 0.38;
      let best = null;
      let bestHeight = Infinity;
      links.forEach(([, hash]) => {
        if (!hash.startsWith('#')) return;
        const el = document.getElementById(hash.slice(1));
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= probe && rect.bottom > probe && rect.height < bestHeight) {
          best = hash;
          bestHeight = rect.height;
        }
      });
      setActive(best);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [links]);

  useEffect(() => {
    document.body.classList.toggle('nav-open', open);
    return () => document.body.classList.remove('nav-open');
  }, [open]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const wide = window.matchMedia('(min-width: 901px)');
    const onWide = (event) => {
      if (event.matches) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    wide.addEventListener('change', onWide);
    return () => {
      window.removeEventListener('keydown', onKey);
      wide.removeEventListener('change', onWide);
    };
  }, []);

  const cta = onPaper
    ? { href: PAPER.pdf, label: 'Download PDF', Icon: ArrowDown, kind: 'btn--download', download: true }
    : null;
  const showInstaller = !onPaper && !atHome && !onBrand;

  const classes = [
    'nav',
    `nav--${open ? 'dark' : tone}`,
    scrolled || open ? 'nav--scrolled' : '',
    open ? 'nav--open' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const close = () => setOpen(false);

  return (
    <header className={classes}>
      <div className="container container--wide nav__inner">
        <a className="nav__brand" href={atHome ? '#top' : '/'} onClick={close} aria-label="QSI Labs, home">
          <BrandMark className="nav__mark" />
          <span className="nav__wordmark">QSI Labs</span>
          {!atHome ? <span className="nav__product">{product}</span> : null}
        </a>
        <button
          className="nav__toggle"
          type="button"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          aria-controls="site-menu"
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
        </button>
        <nav id="site-menu" className="nav__menu" aria-label={menuLabel}>
          <ol className="nav__links">
            {links.map(([label, hash]) => (
              <li key={hash}>
                <a
                  className="nav__link"
                  href={hash}
                  onClick={close}
                  aria-current={active === hash ? 'location' : undefined}
                >
                  {label}
                </a>
              </li>
            ))}
          </ol>
          {cta ? (
            <a
              className={`btn btn--small ${cta.kind} nav__cta`}
              href={cta.href}
              onClick={close}
              download={cta.download ? '' : undefined}
            >
              {cta.label}
              <cta.Icon />
            </a>
          ) : showInstaller ? (
            <DownloadButton className="btn--small nav__cta" short onClick={close} />
          ) : null}
        </nav>
      </div>
    </header>
  );
}
